import { useMemo } from "react"
import { License } from "../constants/license.enum"
import { Role } from "../constants/role.enum"
import { CompanyT } from "../store/slices/company.slice"
import { GraphicAsset } from "../store/slices/graphics.slice"
import { useAppSelector } from "./useRedux"
import { useSession } from "./useSession"
import { UserRole } from "../constants/roles.enum"

export const useVerifyPermission = (graphic: GraphicAsset, selectedCompany?: CompanyT | null) => {
    const user = useAppSelector((state) => state.user.value)
    const { hasRole } = useSession()

    //main const
    const isMyGraphic = graphic.userId === user._id //user create graphic
    const isPartOfWorkspace = graphic.company?.usersRef.some((u) => u.id === user._id) //user is part of workspace
    const isWorkspaceAdmin = graphic.company?.usersRef.some(
        (u) => u.id === user._id && u.role >= Role.Admin //user is Admin of workspace
    )
    const isUserActiveInCompany = selectedCompany?.usersRef.some((u) => u.id === user._id)

    const isVerify =
        graphic.company?.expired === null ||
        (graphic.company?.expired && graphic.company?.expired.getTime() > Date.now())

    const isSelectedCompanyVerify =
        selectedCompany?.expired === null ||
        (selectedCompany?.expired && selectedCompany?.expired.getTime() > Date.now())

    const notEnoughCoins = !!(
        !selectedCompany?.isPrivate &&
        selectedCompany?.coins &&
        selectedCompany?.coins < graphic.price
    )

    //permission const
    const canCreateNew = useMemo(() => {
        if (selectedCompany) {
            if (
                (isSelectedCompanyVerify && isUserActiveInCompany) ||
                hasRole(UserRole.Admin) ||
                selectedCompany.isPrivate
            ) {
                return true
            } else {
                return false
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany])
    const canUpdate =
        (isVerify && isUserActiveInCompany && !graphic.isPublished) || //company is verified, user is Active for selectedCompany and graphic is not Published
        (!isVerify && graphic.company?.isPrivate && !graphic.isPublished) || //user is not verified, graphic is in private company and graphic is not published
        hasRole(UserRole.Admin) //can Update when user is superAdmin

    const canDuplicate =
        (isUserActiveInCompany && isVerify) || //company is verified and user is active
        (!isVerify && graphic.company?.isPrivate) || //company is not verified and company is Private
        hasRole(UserRole.Admin) //user is superAdmin

    const canPublishUnPublish =
        (isVerify && (isMyGraphic || isWorkspaceAdmin)) || //company is verified and graphic is final version and it is my graphic or user is Admin of workspace
        hasRole(UserRole.Admin) //user is superAdmin and graphic is final version

    const canExport =
        graphic.company?.isPrivate || (isVerify && isUserActiveInCompany) || hasRole(UserRole.Admin)

    const canExportPackage =
        (isVerify && isUserActiveInCompany && graphic.company?.license === License.Enterprise) ||
        (graphic.company?.isPrivate &&
            (user.license?.expired === null ||
                (user.license?.expired && user.license?.expired.getTime() > Date.now())))
    hasRole(UserRole.Admin)

    const canDelete = !graphic.isPublished && (isMyGraphic || hasRole(UserRole.Admin))

    const canPurchase = useMemo(() => {
        if (selectedCompany) {
            //selected workspace is not expired
            let notExpired =
                selectedCompany?.expired && selectedCompany?.expired.getTime() > Date.now()
            //is user admin of selected workspace
            let userIsAdminOfWorkspace = selectedCompany?.usersRef.some(
                (u) => u.id === user._id && u.role >= Role.Admin
            )
            let selectedCompanyOwnGraphic = graphic.companyId === selectedCompany._id //selected company created and own this graphic

            if (
                !selectedCompanyOwnGraphic && //selected company DO NOT created and own this graphic
                isUserActiveInCompany && //user is active in selected company
                selectedCompany.coins >= graphic.price && //enough coins
                ((notExpired && userIsAdminOfWorkspace) || //verified, not expired ended date, user is admin of selected workspace
                    (selectedCompany?.isPrivate && graphic.price === 0)) //personal workspace
                // (isWorkspaceAdmin && isVerified && notExpired && userIsAdminOfWorkspace) || //verified
                // (isWorkspaceAdmin && selectedCompany?.isPrivate && graphic.price === 0) //personal workspace
            ) {
                return true
            } else {
                return false
            }
        } else {
            return false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany])

    //when company is exactly in license === 'free', user have only Private workspace,
    //when user have workspace with expired license then graphic in "Editing not available" and can not reach editor
    const isFreeLicense: boolean =
        !!(graphic.company?.isPrivate && user.license?.license === License.Free) ||
        !!(
            graphic.company?.isPrivate &&
            user.license?.expired &&
            user.license?.expired.getTime() < Date.now()
        ) ||
        !!(
            !graphic.company?.isPrivate &&
            user.license?.expired &&
            user.license?.expired.getTime() < Date.now()
        )

    return {
        isMyGraphic,
        isWorkspaceAdmin,
        isPartOfWorkspace,
        isVerify,
        canDuplicate,
        canCreateNew,
        canUpdate,
        canDelete,
        canPublishUnPublish,
        canPurchase,
        canExport,
        canExportPackage,
        notEnoughCoins,
        isFreeLicense,
    }
}
